import React, { useMemo, useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import moment from 'moment';
import { saveAs } from 'file-saver';

type Point = {
    date: string;
    value: number;
    series: string;
};

type EventMark = {
    date: string; // ISO date or datetime
    title: string;
};

type Phase = {
    id: string;
    name: string;
    start: string;
    end?: string;
    color?: string;
};

type Props = {
    title?: string;
    yLabel?: string;
    points: Point[];
    phases?: Phase[];
    events?: EventMark[];
    height?: number;
    isShowHundred?: boolean;
};

const AbaLineChartE: React.FC<Props> = ({
    title = '',
    yLabel = 'Frequency',
    points,
    phases = [],
    events = [],
    height = 420,
    isShowHundred = false,
}) => {

    console.log(points);


    const chartRef = useRef<any>(null);

    const seriesNames = Array.from(new Set((points ?? []).map((p) => p.series)));

    const colorPalette = [
        '#5470C6',
        '#91CC75',
        '#EE6666',
        '#73C0DE',
        '#FAC858',
        '#3BA272',
        '#FC8452',
        '#9A60B4',
        '#EA7CCC',
    ];

    const shortLabel = (name: string, len = 18) => {
        const s = (name || '').trim();
        if (!s) return '';
        return s.length > len ? s.slice(0, len) + '…' : s;
    };

    const maxPointDate = points?.length ? new Date(Math.max(...points.map((p) => +new Date(p.date)))) : new Date();

    const maxPointISO = moment(maxPointDate).format('YYYY-MM-DD');

    // ------------------ Phases ------------------
    const markAreas =
        phases && phases.length > 0
            ? phases
                  .filter((p) => p.start)
                  .map((p) => [
                      {
                          name: shortLabel(p.name, 7),
                          phaseFullName: p.name,
                          xAxis: p.start,
                          itemStyle: {
                              color: p.color || 'rgba(255,255,255,0.04)',
                              borderColor: '#666',
                              borderWidth: 0.5,
                          },
                          tooltip: {
                              show: true,
                              formatter: (params: any) => params?.data?.phaseFullName || params?.name || '',
                          },
                      },
                      { xAxis: p.end ?? maxPointISO },
                  ])
            : [];

    const phaseMarkLineData = (phases ?? [])
        .filter((p) => p?.start)
        .map((p) => ({
            xAxis: p.start,
            kind: 'phase',
            phaseFullName: p.name || '',
            lineStyle: { color: '#444', width: 2, type: 'solid' },
        }));

    const { eventMarkLineData, hasEvents } = useMemo(() => {
        const normalizeKey = (d: string) => moment(d).format('YYYY-MM-DD');

        const map = new Map<string, string[]>();
        for (const e of events ?? []) {
            const title = String(e?.title ?? '').trim();
            const date = e?.date ? String(e.date) : '';
            if (!title || !date) continue;

            const key = normalizeKey(date);
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(title);
        }

        const merged = Array.from(map.entries()).map(([dateKey, titles]) => ({
            date: dateKey,
            title: titles.join(' | '),
        }));

        const data = merged.map((e) => ({
            xAxis: e.date,
            kind: 'event',
            eventTitle: e.title,
            lineStyle: { color: '#000', width: 2, type: 'dotted' },
            z: 999,
            zlevel: 10,
        }));

        return { eventMarkLineData: data, hasEvents: data.length > 0 };
    }, [events]);

    const isSingleSeries = seriesNames.length <= 1;

    const lineSeries = seriesNames.map((name) => ({
        name,
        type: 'line',
        smooth: false,
        showSymbol: true,
        symbolSize: 6,
        lineStyle: { width: 2 },
        data: (points ?? []).filter((p) => p.series === name).map((p) => [p.date, p.value]),

        // phases on each line
        markLine: {
            silent: true,
            symbol: 'none',
            data: phaseMarkLineData,

            label: {
                show: true,
                rotate: 90,
                position: 'insideTop',
                fontSize: 10,
                formatter: (params: any) => shortLabel(params?.data?.phaseFullName ?? '', 7),
            },
            tooltip: {
                show: true,
                formatter: (params: any) => params?.data?.phaseFullName || '',
            },
        },

        markArea: {
            silent: true,
            data: markAreas,
            label: {
                show: true,
                rotate: 90,
                position: 'insideBottomLeft',
                fontSize: 12,
                padding: [0, 0, -10, 10],
                color: '#000',
                formatter: (params: any) => {
                    const full = params?.data?.phaseFullName ?? params?.name ?? params?.data?.name ?? '';
                    return isSingleSeries ? '' : shortLabel(full, 40);
                },
            },
            emphasis: {
                label: {
                    show: true,
                    formatter: (params: any) => params?.data?.phaseFullName ?? params?.name ?? '',
                },
            },
        },
    }));


    const eventsSeries = hasEvents
        ? {
              //name: 'Events',
              type: 'line',
              data: [],
              showSymbol: false,
              lineStyle: { opacity: 0 },
              tooltip: { show: false },

              markLine: {
                  silent: true,
                  symbol: 'none',
                  data: eventMarkLineData,

                  label: {
                      show: true,
                      rotate: 90,
                      position: 'insideStartTop', // insideEndTop
                      distance: 6,
                      fontSize: 11,
                      color: '#111',
                      padding: [2, 4, 2, 4],
                      backgroundColor: 'rgba(255,255,255,0.85)',
                      borderColor: 'rgba(0,0,0,0.25)',
                      borderWidth: 1,
                      formatter: (params: any) => shortLabel(params?.data?.eventTitle ?? '', 40),
                  },
                  tooltip: { show: false },
                  z: 999,
                  zlevel: 10,
              },
          }
        : null;

    const option = {
        color: colorPalette,
        title: {
            show: false,
            text: title,
            left: 'center',
            top: 10,
            textStyle: { fontSize: 16, fontWeight: 'bold' },
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { type: 'cross' },

            formatter: (params: any) => {
                if (!params?.length) return '';

                const date = moment(params[0].value[0]).format('YYYY-MM-DD');

                let html = `<b>${date}</b><br/>`;

                params.forEach((p: any) => {
                    html += `
                <span style="
                    display:inline-block;
                    width:8px;
                    height:8px;
                    background:${p.color};
                    border-radius:50%;
                    margin-right:6px;
                "></span>
                ${p.value[1]}<br/>
            `;
                });

                return html;
            },
        },
        legend: isSingleSeries
            ? { show: false }
            : {
                  type: 'scroll',
                  bottom: 0,
                  left: 'center',
                  orient: 'horizontal',
                  textStyle: { fontSize: 12 },
              },
        grid: { top: 70, left: 60, right: 40, bottom: 80 },
        xAxis: {
            type: 'time',
            axisLabel: {
                hideOverlap: true,
                formatter: (value: string) => moment(value).format('MM/DD/YYYY'),
            },
            splitLine: { show: true },
            axisLine: { lineStyle: { color: '#888' } },
        },
        yAxis: {
            type: 'value',
            name: yLabel,
            nameLocation: 'middle',
            nameGap: 40,
            splitLine: { show: true },
            axisLine: { lineStyle: { color: '#888' } },
            min: 0,
            max: isShowHundred ? (value: { max: number }) => Math.max(100, value.max) : undefined,
        },

        series: eventsSeries ? [...lineSeries, eventsSeries] : lineSeries,
    };

    const handleDownload = () => {
        const chart = chartRef.current?.getEchartsInstance?.();
        if (!chart) return;

        chart.setOption(
            {
                title: {
                    show: true,
                    text: title,
                    left: 'center',
                    top: 10,
                    textStyle: { fontSize: 16, fontWeight: 'bold' },
                },
            },
            { notMerge: false, lazyUpdate: false, silent: true },
        );

        const dataUrl = chart.getDataURL({
            type: 'png',
            pixelRatio: 2,
            backgroundColor: '#ffffff',
        });

        chart.setOption({ title: { show: false } }, { notMerge: false, lazyUpdate: false, silent: true });

        saveAs(dataUrl, `${title || 'chart'}.png`);
    };

    return (
        <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <button
                    onClick={handleDownload}
                    style={{
                        background: '#2b71e8',
                        color: 'white',
                        border: 'none',
                        borderRadius: 4,
                        padding: '4px 10px',
                        fontSize: 13,
                        cursor: 'pointer',
                    }}
                >
                    ⬇ Download
                </button>
            </div>

            <ReactECharts
                ref={chartRef}
                option={option}
                style={{ height, width: '100%' }}
                notMerge={true}
                lazyUpdate={true}
            />
        </div>
    );
};

export default AbaLineChartE;
